import type { DashboardData } from "./types";

const API_BASE_URL = typeof window === "undefined" ? "http://127.0.0.1:8000" : "";

async function getJson<T>(path: string, authToken?: string): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    cache: "no-store",
    credentials: "include",
    ...(authToken ? { headers: { "x-dashboard-auth": authToken } } : {})
  });
  if (!response.ok) throw new Error(`Failed to load ${path}: ${response.status}`);
  return response.json() as Promise<T>;
}

function settledValue<T>(result: PromiseSettledResult<T>, fallback: T): T {
  return result.status === "fulfilled" ? result.value : fallback;
}

export const API_BASE = API_BASE_URL;

export type DashboardProvenance = {
  overview: boolean;
  manufacturing: boolean;
  markets: boolean;
  centralBanks: boolean;
  crypto: boolean;
  collector: boolean;
};

export type DashboardLoadResult = {
  data: DashboardData;
  provenance: DashboardProvenance;
};

export function dashboardNeedsRefresh(data: DashboardData) {
  const hasPublicOecd = data.manufacturing.series.some((series) => series.key === "us_oecd_cli" && series.points.length > 0);
  return !hasPublicOecd || data.markets.performance.length === 0 || (data.crypto.btc_spot?.length ?? 0) === 0;
}

export async function getDashboardLoadResult(authToken?: string): Promise<DashboardLoadResult> {
  const [manufacturing, markets, crypto, collector] = await Promise.allSettled([
    getJson<DashboardData["manufacturing"]>("/api/manufacturing", authToken),
    getJson<DashboardData["markets"]>("/api/markets", authToken),
    getJson<DashboardData["crypto"]>("/api/crypto", authToken),
    getJson<DashboardData["collector"]>("/api/collector/status", authToken)
  ]);

  const data = {
    ...fallbackDashboardData,
    overview: fallbackDashboardData.overview,
    manufacturing: settledValue(manufacturing, fallbackDashboardData.manufacturing),
    markets: settledValue(markets, fallbackDashboardData.markets),
    centralBanks: fallbackDashboardData.centralBanks,
    crypto: settledValue(crypto, fallbackDashboardData.crypto),
    collector: settledValue(collector, fallbackDashboardData.collector)
  };
  return {
    data,
    provenance: {
      overview: false,
      manufacturing: manufacturing.status === "fulfilled",
      markets: markets.status === "fulfilled",
      centralBanks: false,
      crypto: crypto.status === "fulfilled",
      collector: collector.status === "fulfilled"
    }
  };
}

export async function getDashboardData(authToken?: string): Promise<DashboardData> {
  return (await getDashboardLoadResult(authToken)).data;
}

export const fallbackDashboardData: DashboardData = {
  overview: { as_of: "", cards: [] },
  manufacturing: {
    status: "unavailable",
    source_note: "Validated manufacturing data is unavailable.",
    series: [],
    comparison_charts: [],
    formula_notes: [],
    diffusion: []
  },
  markets: {
    periods: ["1D", "1W", "1M", "3M"],
    performance: [],
    roro: {
      label: "Risk-on / Risk-off Regime",
      value: "Unavailable",
      confidence: 0,
      series: []
    },
    watchlist: [],
    fomc: {
      title: "FOMC Statement Index",
      note: "Unavailable",
      source_url: "https://www.federalreserve.gov/monetarypolicy/fomccalendars.htm",
      series: []
    },
    pboc_balance_sheet_assets: { series: {} },
    etf_source: {
      name: "SoSoValue",
      mode: "api",
      status: "unavailable",
      url: "https://sosovalue-1.gitbook.io/sosovalue-api-doc/2.-etf/summary-history"
    },
    source_note: "Live market data is unavailable."
  },
  centralBanks: {
    available: false,
    status: "unavailable",
    message: "Actual OIS history is not configured.",
    snapshot_date: "",
    global_score: 0,
    global_regime: "UNAVAILABLE",
    divergence_flag: false,
    results: [],
    forward_curve: [],
    ois_to_current: [],
    classifier_config: { threshold_bps: 5, lookback_days: 20 }
  },
  crypto: {
    periods: ["1W", "1M", "3M", "6M"],
    open_interest: [],
    btc_spot: [],
    vol_term_structure: {
      label: "ATM Volatility Term Structure",
      source_url: "https://www.deribit.com/",
      tenors: []
    },
    skew_25d: {
      label: "25D Skew",
      source_url: "https://www.deribit.com/",
      tenors: []
    },
    liquidations: null,
    sources: []
  },
  research: { status: "not_configured", monitors: [], macro_links: [] },
  sources: { policy: "Only validated live data is rendered.", items: [], discovery_targets: [] },
  collector: { run: null }
};
